import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Switch,
} from "antd";
import { FormattedMessage } from "react-intl";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { countryService } from "../../../shared/services/country";
import { priceBracketService } from "../../../shared/services/price-brackets";
import MainUtils from "../../../shared/utils/main";
import { omit, omitBy } from "lodash";
import {
  CreateOrUpdatePriceBracketDto,
  PriceBracketDto,
} from "../../../shared/services/price-brackets/dto";

interface PriceBracket {
  id: string;
  name: string;
  price: number;
  isActive: boolean;
  perCount: number;
  countries: string[];
}

interface RouteParams {
  id?: string;
}

const CreateUpdatePriceBracket: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const isEdit = !!id;
  const [countries, setCountries] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    if (isEdit) {
      fetchPriceBracket();
    }
  }, [id]);

  useEffect(() => {
    countryService.getAllCountries().then((ctrs) => {
      setCountries(
        ctrs.Data.map((country) => ({
          label: country.name,
          value: country.id,
        })),
      );
    });
  }, []);

  const fetchPriceBracket = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const priceBracket = await priceBracketService.getPriceBracket(id);
      if (priceBracket) {
        form.setFieldsValue({
          ...priceBracket.Data,
          countries: priceBracket.Data.countries.map((country) => country.id),
        });
      } else {
        message.error("Price bracket not found");
        navigate("/price-brackets");
      }
    } catch (error) {
      message.error("Failed to fetch price bracket details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (
    values: CreateOrUpdatePriceBracketDto & {
      countries: string[];
    },
  ) => {
    try {
      new Promise<PriceBracketDto>((resolve, reject) => {
        const apiCall = MainUtils.isEmptyValue(id)
          ? priceBracketService.createPriceBracket(omit(values, "countries"))
          : priceBracketService.updatePriceBracket({
              ...omit(values, "countries"),
              id: id!,
            });

        apiCall
          .then((response) => {
            if (!MainUtils.isEmptyValue(response?.Data)) {
              resolve(response.Data);
            } else {
              reject(new Error("No data returned from API"));
            }
          })
          .catch((error) => {
            reject(error);
          });
      })
        .then((bracket: PriceBracketDto) => {
          return priceBracketService.updatePriceBracketCountries({
            id: bracket.id,
            countries: values.countries,
          });
        })
        .then(() => {
          navigate("/price-brackets");
        })
        .catch((error) => {
          console.error("Error in price bracket operation:", error);
          // Handle error appropriately (show toast, etc.)
        });
    } catch (error) {
      console.error(error);
    } finally {
    }
  };

  return (
    <Card
      title={
        <>
          <Button
            icon={<ArrowLeftOutlined />}
            type="link"
            onClick={() => navigate("/price-brackets")}
            style={{ marginRight: 16 }}
          />
          {isEdit ? (
            <FormattedMessage
              id="priceBrackets.edit"
              defaultMessage="Edit Price Bracket"
            />
          ) : (
            <FormattedMessage
              id="priceBrackets.create"
              defaultMessage="Create Price Bracket"
            />
          )}
        </>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          isActive: true,
          perCount: 1,
          countries: [],
        }}
        disabled={loading}
      >
        <Form.Item
          name="name"
          label={
            <FormattedMessage id="priceBrackets.name" defaultMessage="Name" />
          }
          rules={[
            { required: true, message: "Please enter the price bracket name" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="price"
          label={
            <FormattedMessage id="priceBrackets.price" defaultMessage="Price" />
          }
          rules={[{ required: true, message: "Please enter the price" }]}
        >
          <InputNumber
            min={0}
            precision={2}
            style={{ width: "100%" }}
            prefix="$"
          />
        </Form.Item>

        <Form.Item
          name="perCount"
          label={
            <FormattedMessage
              id="priceBrackets.perCount"
              defaultMessage="Per Count"
            />
          }
          tooltip="Number of items this price applies to"
          rules={[{ required: true, message: "Please enter the count" }]}
        >
          <InputNumber min={1} precision={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="isActive"
          label={
            <FormattedMessage
              id="priceBrackets.status"
              defaultMessage="Status"
            />
          }
          valuePropName="checked"
        >
          <Switch
            checkedChildren={
              <FormattedMessage id="common.active" defaultMessage="Active" />
            }
            unCheckedChildren={
              <FormattedMessage
                id="common.inactive"
                defaultMessage="Inactive"
              />
            }
          />
        </Form.Item>

        <Form.Item
          name="countries"
          label={
            <FormattedMessage
              id="priceBrackets.countries"
              defaultMessage="Countries"
            />
          }
          rules={[
            { required: true, message: "Please select at least one country" },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Select countries"
            style={{ width: "100%" }}
            options={countries}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
            {isEdit ? (
              <FormattedMessage id="common.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="common.create" defaultMessage="Create" />
            )}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default CreateUpdatePriceBracket;
